import { useState, useEffect } from 'react';
import { Lightbulb, Search, Copy, CheckCircle, ExternalLink } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface CoachingKnowledge {
  article_id: string;
  title: string;
  content: string;
  relevance_score: number;
  coaching_notes?: string;
}

interface KnowledgeBasePanelProps {
  callContext: {
    scenario?: string;
    customerObjection?: string;
    serviceType?: string;
    agentSentiment?: string;
  };
}

export function KnowledgeBasePanel({ callContext }: KnowledgeBasePanelProps) {
  const { theme } = useTheme();
  const [knowledge, setKnowledge] = useState<CoachingKnowledge[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (callContext.scenario || callContext.customerObjection || callContext.serviceType) {
      fetchRelevantKnowledge();
    }
  }, [callContext]);

  const fetchRelevantKnowledge = async () => {
    setIsLoading(true);

    try {
      const contextQueries = [];

      if (callContext.customerObjection) {
        contextQueries.push({
          context_type: 'call_scenario',
          context_value: callContext.customerObjection
        });
      }

      if (callContext.serviceType) {
        contextQueries.push({
          context_type: 'service_type',
          context_value: callContext.serviceType
        });
      }

      if (callContext.scenario) {
        contextQueries.push({
          context_type: 'call_scenario',
          context_value: callContext.scenario
        });
      }

      const knowledgePromises = contextQueries.map(({ context_type, context_value }) =>
        fetch(`/api/knowledge/coaching?context_type=${context_type}&context_value=${encodeURIComponent(context_value)}&limit=3`)
          .then(res => res.json())
          .then(data => data.knowledge || [])
      );

      const knowledgeResults = await Promise.all(knowledgePromises);
      const allKnowledge = knowledgeResults.flat();

      const uniqueKnowledge = allKnowledge
        .filter((item, index, self) => self.findIndex(k => k.article_id === item.article_id) === index)
        .sort((a, b) => b.relevance_score - a.relevance_score)
        .slice(0, 3);

      setKnowledge(uniqueKnowledge);
    } catch (error) {
      console.error('Error fetching coaching knowledge:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToClipboard = async (content: string, id: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  if (!callContext || (!callContext.scenario && !callContext.customerObjection && !callContext.serviceType)) {
    return null;
  }

  return (
    <div
      style={{
        backgroundColor: theme.colors.bgCard,
        borderTop: `1px solid ${theme.colors.borderColor}`,
        borderBottom: `1px solid ${theme.colors.borderColor}`
      }}
    >
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb className="w-4 h-4" style={{ color: theme.colors.warningText }} />
          <h3 className="text-sm font-semibold" style={{ color: theme.colors.textPrimary }}>
            Knowledge Base
          </h3>
        </div>
        <p className="text-xs" style={{ color: theme.colors.textSecondary }}>
          Relevant information for this call
        </p>
      </div>

      <div className="px-4 pb-3">
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <div 
              className="animate-spin rounded-full h-5 w-5 border-b-2"
              style={{ borderColor: theme.colors.primaryColor }}
            />
            <span className="ml-2 text-xs" style={{ color: theme.colors.textSecondary }}>
              Finding relevant knowledge...
            </span>
          </div>
        )}

        {knowledge.length > 0 && (
          <div className="space-y-2">
            {knowledge.map((item) => (
              <div
                key={item.article_id}
                className="rounded p-2"
                style={{
                  backgroundColor: theme.colors.bgPage,
                  border: `1px solid ${theme.colors.borderColor}`
                }}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex-1">
                    <h4 className="text-xs font-semibold" style={{ color: theme.colors.textPrimary }}>
                      {item.title}
                    </h4>
                    {item.coaching_notes && (
                      <p className="text-xs mt-1" style={{ color: theme.colors.infoText }}>
                        💡 {item.coaching_notes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <span
                      className="px-1.5 py-0.5 text-xs rounded"
                      style={{
                        backgroundColor: theme.colors.infoBg,
                        color: theme.colors.infoText
                      }}
                    >
                      {Math.round(item.relevance_score * 100)}%
                    </span>
                    <button
                      onClick={() => handleCopyToClipboard(item.content, item.article_id)}
                      className="p-1 rounded transition-colors"
                      style={{ color: theme.colors.textSecondary }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      title="Copy to clipboard"
                    >
                      {copiedId === item.article_id ? (
                        <CheckCircle className="w-3 h-3" style={{ color: theme.colors.successText }} />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                    </button>
                  </div>
                </div>

                <p className="text-xs line-clamp-2" style={{ color: theme.colors.textSecondary }}>
                  {item.content.length > 120
                    ? `${item.content.substring(0, 120)}...`
                    : item.content}
                </p>
              </div>
            ))}
          </div>
        )}

        {knowledge.length === 0 && !isLoading && (
          <div className="text-center py-4">
            <Search className="w-6 h-6 mx-auto mb-2" style={{ color: theme.colors.textTertiary }} />
            <p className="text-xs" style={{ color: theme.colors.textSecondary }}>
              No specific suggestions for this context
            </p>
          </div>
        )}

        <button
          onClick={() => window.open('/agent/knowledge-search', '_blank')}
          className="w-full mt-2 px-3 py-1.5 rounded text-xs flex items-center justify-center gap-1 transition-colors"
          style={{
            backgroundColor: theme.colors.bgPage,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.textSecondary
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgPage}
        >
          <ExternalLink className="w-3 h-3" />
          Open Full Knowledge Base
        </button>
      </div>
    </div>
  );
}
